import React, { useEffect, useRef, useState } from "react";
import "./Feedbackpopupcdmqb.css";
// CDMQB Feedback popup, opened from the toolbar's Feedback button. Same
// content and request as Report Bug/Feedback in Account Settings, laid out
// like the CDMQB Exit Case popup. All classNames are suffixed with "CDMQB"
// so they never collide with the default skin's CSS.
//
// Props:
//   displayFeedback  bool   - whether the popup is open
//   closeFeedback    func   - closes the popup
//   submitFeedback   func   - async (feedbackText) => void; throws on failure

export default function FeedbackPopupCDMQB(props) {
  const [feedback, setFeedback] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const textAreaRef = useRef(null);

  // Each time the popup opens: clear any old error / thank-you message and
  // put the cursor in the text box. Unsent text is kept, so closing by
  // accident doesn't lose it.
  useEffect(() => {
    if (props.displayFeedback) {
      setError(null);
      setSubmitted(false);
      // Wait a frame so the popup is visible before focusing.
      const frame = window.requestAnimationFrame(() => {
        if (textAreaRef.current) textAreaRef.current.focus();
      });
      return () => window.cancelAnimationFrame(frame);
    }
  }, [props.displayFeedback]);

  const close = () => {
    if (loading) return;
    props.closeFeedback();
  };

  const submit = async () => {
    if (loading) return;
    if (!feedback.trim()) {
      setError("Please enter your feedback before submitting.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await props.submitFeedback(feedback);
      setFeedback("");
      setSubmitted(true);
    } catch (err) {
      setError(`${err}`);
    } finally {
      setLoading(false);
    }
  };

  // Keys typed in the popup stay in the popup, so the page's own keyboard
  // shortcuts (Alt+H, Alt+S, ...) don't fire while writing. Escape closes.
  const handleKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    }
  };

  return (
    <div
      className={`feedbackPageContainerCDMQB ${
        props.displayFeedback ? "" : "hideFeedbackPopupCDMQB"
      }`}
      aria-hidden={!props.displayFeedback}
      onKeyDown={handleKeyDown}
    >
      <div className="feedbackBackdropCDMQB" onClick={close} />
      <div
        className="feedbackContainerCDMQB"
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedbackHeaderTextCDMQB"
      >
        <div className="feedbackHeaderContainerCDMQB">
          <p className="feedbackHeaderTextCDMQB" id="feedbackHeaderTextCDMQB">
            Feedback
          </p>
        </div>

        <div className="feedbackBodyContainerCDMQB">
          {submitted ? (
            <p className="feedbackBodyTextCDMQB feedbackSuccessTextCDMQB">
              Thank you for your feedback.
            </p>
          ) : (
            <>
              <p className="feedbackBodyTextCDMQB">
                Tell us what could be improved about this question or
                explanation.
              </p>
              <label
                htmlFor="feedbackTextAreaCDMQB"
                className="feedbackLabelCDMQB"
              >
                Enter Feedback
              </label>
              <textarea
                ref={textAreaRef}
                id="feedbackTextAreaCDMQB"
                className="feedbackTextAreaCDMQB"
                value={feedback}
                onChange={(e) => {
                  setFeedback(e.target.value);
                  if (error) setError(null);
                }}
                disabled={loading}
                autoComplete="off"
              />
              {error && <p className="feedbackErrorTextCDMQB">{error}</p>}
            </>
          )}
        </div>

        <div className="feedbackFooterContainerCDMQB">
          {submitted ? (
            <button
              type="button"
              className="feedbackFooterButtonCDMQB"
              onClick={close}
            >
              Close
            </button>
          ) : (
            <>
              <button
                type="button"
                className="feedbackFooterButtonCDMQB"
                onClick={close}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="feedbackFooterButtonCDMQB"
                onClick={submit}
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
